// routes/index.tsx
import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { LoadingOverlay } from "@mantine/core";
import { paths } from "./paths";
import Login from "@admin/pages/login";
import { AuthGuard } from "@admin/guards/auth-guard";
import { GuestGuard } from "@admin/guards/guest-guard";
import { PageTitle } from "@admin/components/page-title";

// ------------------Layouts------------------
const DashboardLayout = lazy(() => import("@admin/layouts/dashboard"));
const FullScreenLayout = lazy(() => import("@admin/layouts/fullscreen"));
const AuthLayout = lazy(() => import("@admin/layouts/auth"));
const ErrorLayout = lazy(() => import("@admin/layouts/error"));

// ------------------ lazy loaded pages------------------

//auth
const ForgotPassword = lazy(() => import("@admin/pages/forgot-password"));

const Dashboard = lazy(() => import("@admin/pages/dashboard"));
// scheduling
const Scheduler = lazy(
	() => import("@admin/pages/scheduling/scheduler/scheduler"),
);
const SpreadsheetReport = lazy(
	() => import("@admin/pages/scheduling/spreadsheet-view/spreadsheet-report"),
);
const AutoAssignment = lazy(
	() => import("@admin/pages/scheduling/auto-assignment/auto-assignment"),
);
const Proposals = lazy(() => import("@admin/pages/scheduling/proposals"));
const Confirmations = lazy(
	() => import("@admin/pages/scheduling/confirmations/confirmations"),
);
const TourLeaderActions = lazy(
	() =>
		import("@admin/pages/scheduling/tour-leader-actions/tour-leader-actions-list"),
);
const TourLeaderActionsEdit = lazy(
	() =>
		import("@admin/pages/scheduling/tour-leader-actions/tour-leader-actions-edit"),
);

//account
const ChangePassword = lazy(
	() => import("@admin/pages/account/change-password"),
);

//management
const UserList = lazy(() => import("@admin/pages/management/users/user-list"));
const UserEdit = lazy(() => import("@admin/pages/management/users/user-edit"));
const UserCreate = lazy(
	() => import("@admin/pages/management/users/user-create"),
);
const Tours = lazy(() => import("@admin/pages/management/tours/tour-list"));
const TourEdit = lazy(() => import("@admin/pages/management/tours/tour-edit"));
const TourLeaders = lazy(
	() => import("@admin/pages/management/tour-leaders/tour-leader-list-1"),
);
const TourLeaderEdit = lazy(
	() => import("@admin/pages/management/tour-leaders/edit/tour-leader-edit"),
);
const Departures = lazy(
	() => import("@admin/pages/management/departures/departure-list"),
);
const DepartureEdit = lazy(
	() => import("@admin/pages/management/departures/departure-edit"),
);
const SystemSettings = lazy(
	() => import("@admin/pages/management/system-settings"),
);
const SeasonManagement = lazy(
	() => import("@admin/pages/management/season-management"),
);

//logs
const AuditLogs = lazy(
	() => import("@admin/pages/logs/audit-logs/audit-logs-list"),
);
const DataImportLogs = lazy(
	() => import("@admin/pages/logs/data-imports/data-import-list"),
);

//data imports
const DataImports = lazy(() => import("@admin/pages/data-imports"));

//reports
const Reports = lazy(() => import("@admin/pages/reports/reports"));

//documentation
const Documentation = lazy(() => import("@admin/pages/documentation"));

const NotFound = lazy(() => import("@admin/pages/not-found"));

// Wrapper component for lazy-loaded pages
interface LazyPageProps {
	component: React.ComponentType;
	title: string;
}

const LazyPage: React.FC<LazyPageProps> = ({ component: Component, title }) => (
	<>
		<PageTitle name={title} />
		<Suspense fallback={<LoadingOverlay visible />}>
			<Component />
		</Suspense>
	</>
);

const AppRoutes: React.FC = () => {
	return (
		<Routes>
			{/* -------------------- Guest Only ----------------------- */}

			<Route
				element={
					<Suspense fallback={<LoadingOverlay visible />}>
						<AuthLayout />
					</Suspense>
				}
			>
				<Route
					path={paths.auth.root}
					element={
						<GuestGuard>
							<LazyPage
								component={Login}
								title="Login"
								key={location.pathname}
							/>
						</GuestGuard>
					}
				/>
				<Route
					path={paths.auth.forgotPassword}
					element={
						<GuestGuard>
							<LazyPage
								component={ForgotPassword}
								title="Forgot Password"
								key={location.pathname}
							/>
						</GuestGuard>
					}
				/>
			</Route>

			{/* -------------------- Auth Routes ----------------------- */}

			{/* -------------------- Scheduling ----------------------- */}

			<Route
				element={
					<Suspense fallback={<LoadingOverlay visible />}>
						<FullScreenLayout />
					</Suspense>
				}
			>
				<Route
					path={paths.scheduling.scheduler}
					element={
						<AuthGuard>
							<LazyPage
								component={Scheduler}
								title="Scheduler"
								key={location.pathname}
							/>
						</AuthGuard>
					}
				/>

				<Route
					path={paths.scheduling.spreadsheetReport}
					element={
						<AuthGuard>
							<LazyPage component={SpreadsheetReport} title="Spreadsheet" />
						</AuthGuard>
					}
				/>
			</Route>

			<Route
				element={
					<Suspense fallback={<LoadingOverlay visible />}>
						<FullScreenLayout />
					</Suspense>
				}
			></Route>

			<Route
				element={
					<Suspense fallback={<LoadingOverlay visible />}>
						<DashboardLayout />
					</Suspense>
				}
			>
				<Route
					path={paths.dashboard.root}
					element={
						<AuthGuard>
							<LazyPage component={Dashboard} title="Dashboard" />
						</AuthGuard>
					}
				/>

				{/* ------------Auto Assignment Section ------- */}
				<Route path={paths.scheduling.autoAssignment.regular}>
					<Route
						index
						element={
							<AuthGuard>
								<LazyPage component={AutoAssignment} title="Assignments" />
							</AuthGuard>
						}
					/>
					<Route
						path=":subTab"
						element={
							<AuthGuard>
								<LazyPage component={AutoAssignment} title="Assignments" />
							</AuthGuard>
						}
					/>
				</Route>

				<Route path={paths.scheduling.autoAssignment.roadScholar}>
					<Route
						index
						element={
							<AuthGuard>
								<LazyPage component={AutoAssignment} title="RS Assignments" />
							</AuthGuard>
						}
					/>
					<Route
						path=":subTab"
						element={
							<AuthGuard>
								<LazyPage component={AutoAssignment} title="RS Assignments" />
							</AuthGuard>
						}
					/>
				</Route>

				<Route
					path={paths.scheduling.proposals}
					element={
						<AuthGuard>
							<LazyPage component={Proposals} title="Proposals" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.scheduling.confirmations}
					element={
						<AuthGuard>
							<LazyPage component={Confirmations} title="Confirmations" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.scheduling.tourLeaderActions.list}
					element={
						<AuthGuard>
							<LazyPage component={TourLeaderActions} title="TL Actions" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.scheduling.tourLeaderActions.edit}
					element={
						<AuthGuard>
							<LazyPage component={TourLeaderActionsEdit} title="TL Actions" />
						</AuthGuard>
					}
				/>

				{/* -------------------- Reports Routes --------------------- */}
				<Route
					path={paths.reports.root}
					element={
						<AuthGuard>
							<LazyPage component={Reports} title="Reports" />
						</AuthGuard>
					}
				/>

				{/* ------------------------Management Routes -------------------- */}
				<Route
					path={paths.management.users.list}
					element={
						<AuthGuard>
							<LazyPage component={UserList} title="System Users" />
						</AuthGuard>
					}
				/>
				<Route
					path={paths.management.users.edit}
					element={
						<AuthGuard>
							<LazyPage component={UserEdit} title="Edit System User" />
						</AuthGuard>
					}
				/>
				<Route
					path={paths.management.users.create}
					element={
						<AuthGuard>
							<LazyPage component={UserCreate} title="Create System User" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.management.tourLeaders.list}
					element={
						<AuthGuard>
							<LazyPage component={TourLeaders} title="Tour Leaders" />
						</AuthGuard>
					}
				/>
				<Route
					path={paths.management.tourLeaders.edit}
					element={
						<AuthGuard>
							<LazyPage
								component={TourLeaderEdit}
								title="Tour Leader Management"
							/>
						</AuthGuard>
					}
				/>

				<Route
					path={paths.management.tours.list}
					element={
						<AuthGuard>
							<LazyPage component={Tours} title="Trips" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.management.tours.edit}
					element={
						<AuthGuard>
							<LazyPage component={TourEdit} title="Edit Trip" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.management.departures.list}
					element={
						<AuthGuard>
							<LazyPage component={Departures} title="Departures" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.management.departures.edit}
					element={
						<AuthGuard>
							<LazyPage component={DepartureEdit} title="Edit Departure" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.management.systemSettings}
					element={
						<AuthGuard>
							<LazyPage component={SystemSettings} title="System Settings" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.management.seasonManagement}
					element={
						<AuthGuard>
							<LazyPage component={SeasonManagement} title="Season Management" />
						</AuthGuard>
					}
				/>

				{/* -------------------------  Log Routes ------------------------------ */}
				<Route
					path={paths.logs.auditLogs}
					element={
						<AuthGuard>
							<LazyPage component={AuditLogs} title="Audit Logs" />
						</AuthGuard>
					}
				/>

				<Route
					path={paths.logs.dataImportLogs}
					element={
						<AuthGuard>
							<LazyPage component={DataImportLogs} title="Data Import Logs" />
						</AuthGuard>
					}
				/>
				{/* ------------------------- Account Routes ------------------------------ */}
				<Route
					path={paths.account.changePassword}
					element={
						<AuthGuard>
							<LazyPage component={ChangePassword} title="Change Password" />
						</AuthGuard>
					}
				/>

				{/* ----------------------- Documentation Routes ---------------------------- */}
				<Route
					path={paths.documentation.root}
					element={
						<AuthGuard>
							<LazyPage component={Documentation} title="Documentation" />
						</AuthGuard>
					}
				/>

				{/* Data Imports Routes */}
				<Route
					path={paths.dataImports.root}
					element={
						<AuthGuard>
							<LazyPage component={DataImports} title="Data Imports" />
						</AuthGuard>
					}
				/>

				{/* Authenticated catch-all route - Only shown to authenticated users */}
				<Route
					path="auth-404"
					element={
						<AuthGuard>
							<LazyPage component={NotFound} title="Page Not Found" />
						</AuthGuard>
					}
				/>
			</Route>

			{/* Error Layout - Does not require authentication */}
			<Route
				element={
					<Suspense fallback={<LoadingOverlay visible />}>
						<ErrorLayout />
					</Suspense>
				}
			>
				{/* Global catch-all route - shown to anyone, no auth check */}
				<Route
					path="*"
					element={<LazyPage component={NotFound} title="Page Not Found" />}
				/>
			</Route>
		</Routes>
	);
};

export default AppRoutes;
